import { ScrollText, Trash2, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useGetConsultationList } from '@/hooks/queries/chat'
import { useDeleteConsultation } from '@/hooks/queries/chat/mutation'
import ToastMessage from '@/components/common/toast/ToastMessage'
// import TagItem from '../common/tag/TagItem'
import type { ConsultationListItemState } from '@/api/model/chatType'
import { getConversationReportStatus } from '@/api/chatApi'
import { openNativeChatPage } from '@/utils/webViewBridge'

type IToastState = 'deleteChatSuccess' | 'deleteChatFailed' | 'noReport' | 'reportCheckFailed' | 'reportInProgress'

const TOAST_MESSAGE: Record<IToastState, string> = {
  deleteChatSuccess: '대화 삭제 완료',
  deleteChatFailed: '대화 삭제 실패',
  noReport: '해당 리포트는 삭제되었습니다',
  reportCheckFailed: '리포트 상태 조회 실패',
  reportInProgress: '리포트를 생성하고 있습니다!'
}

const ChatHistorySidebar = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [chatToDelete, setChatToDelete] = useState<ConsultationListItemState | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showToast, setShowToast] = useState<IToastState | null>(null)
  const {
    data: consultationData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error
  } = useGetConsultationList()

  const { mutate: deleteConsultation } = useDeleteConsultation()

  // 스크롤 이벤트 리스너 (무한 스크롤)
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current || !hasNextPage || isFetchingNextPage) return

      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        fetchNextPage()
      }
    }

    const scrollElement = scrollRef.current
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll)
      return () => scrollElement.removeEventListener('scroll', handleScroll)
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Window focus 이벤트 리스너 (Native 채팅 종료 후 자동 새로고침)
  useEffect(() => {
    const handleFocus = () => {
      console.log('[ChatHistorySidebar] 포커스 감지 - 채팅 목록 새로고침')
      queryClient.invalidateQueries({ queryKey: ['consultationList'] })
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [queryClient])

  const openDeleteModal = (chat: ConsultationListItemState) => {
    setChatToDelete(chat)
    setShowDeleteModal(true)
  }

  const closeDeleteModal = () => {
    setChatToDelete(null)
    setShowDeleteModal(false)
  }

  const confirmDelete = () => {
    if (chatToDelete) {
      deleteConsultation(
        { consultation_id: Number(chatToDelete.id) },
        {
          onSuccess: () => {
            setShowToast('deleteChatSuccess')
          },
          onError: () => {
            setShowToast('deleteChatFailed')
          }
        }
      )
    }
    closeDeleteModal()
  }

  const handleToastClose = () => {
    setShowToast(null)
  }

  const handleClickReportButton = async (consultation_id: number) => {
    const response = await getConversationReportStatus(consultation_id)
    try {
      if (!response.report_id || response.is_deleted) {
        setShowToast('noReport')
      } else if (!response.is_complete) {
        setShowToast('reportInProgress')
      } else {
        navigate(`/report/${response.report_id}`)
      }
    } catch (error) {
      console.error(error)
      setShowToast('reportCheckFailed')
    }
  }

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className='fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-300 ease-in-out'
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-[calc(100vh-76px)] w-4/5 max-w-sm bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className='flex flex-col h-full'>
          <div className='flex justify-between items-center p-4 border-b border-gray-200'>
            <h2 className='text-lg font-semibold text-gray-700'>대화 목록</h2>
            <button onClick={onClose} className='p-1 text-gray-500 hover:text-gray-700'>
              <X className='w-6 h-6' />
            </button>
          </div>

          <div ref={scrollRef} className='flex-1 overflow-y-auto p-4 space-y-6'>
            {isLoading && <div className='text-center text-gray-500'>로딩 중...</div>}
            {error && <div className='text-center text-red-500'>오류가 발생했습니다.</div>}
            {consultationData && Object.keys(consultationData).length === 0 && (
              <div className='text-center text-gray-500'>상담 내역이 없습니다.</div>
            )}
            {consultationData &&
              Object.entries(consultationData).map(([periodKey, chats]) => {
                let periodTitle = ''
                if (periodKey === 'today') periodTitle = '오늘'
                else if (periodKey === 'last7Days') periodTitle = '지난 7일'
                else if (periodKey === 'last30Days') periodTitle = '지난 30일'
                else if (periodKey === 'previous') periodTitle = '이전'

                const chatList = chats as ConsultationListItemState[]

                return (
                  <div key={periodKey}>
                    <h3 className='text-sm font-medium text-accent mb-2'>{periodTitle}</h3>
                    <ul className='space-y-1'>
                      {chatList.map(chat => (
                        <li
                          key={`chat-list-item_${chat.id}_${chat.title}`}
                          className='flex justify-between items-center p-2 rounded-md hover:bg-gray-100 group'
                        >
                          {chat.type === '주제상담' && chat.status === 'completed' && (
                            <span
                              className='text-sm text-main truncate pr-2 cursor-pointer'
                              onClick={() => handleClickReportButton(Number(chat.id))}
                            >
                              <ScrollText className='w-4 h-4' />
                            </span>
                          )}
                          <span
                            onClick={() => {
                              if (['completed', 'paused'].includes(chat.status)) {
                                // 완료된 상담은 웹뷰에서 처리
                                navigate(`/chat-detail/${chat.id}`)
                              } else {
                                // 진행 중인 상담은 브릿지 사용
                                openNativeChatPage(Number(chat.id), '/chat')
                              }
                            }}
                            className='text-sm text-gray-700 truncate flex-1 pr-2 cursor-pointer'
                          >
                            {chat.title}
                          </span>

                          {(chat.status === 'completed' || chat.status === 'paused') && (
                            <button
                              onClick={() => openDeleteModal(chat)}
                              className='p-1 text-gray-400 hover:text-red-500 transition-colors'
                              aria-label='대화 삭제'
                            >
                              <Trash2 className='w-4 h-4' />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })}

            {isFetchingNextPage && (
              <div className='flex justify-center pt-4'>
                <div className='text-sm text-gray-500'>로딩 중...</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 토스트 메시지 */}
      {showToast && (
        <ToastMessage
          message={TOAST_MESSAGE[showToast as IToastState]}
          isVisible={!!showToast}
          onClose={handleToastClose}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && chatToDelete && (
        <div className='fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]'>
          <div className='bg-white rounded-2xl shadow-xl p-6 w-11/12 max-w-xs mx-auto border border-amber-200'>
            <h2 className='text-lg font-semibold text-amber-900 text-center mb-6'>
              '{chatToDelete.title}' 상담을 삭제하시겠습니까?
            </h2>
            <div className='space-y-3'>
              <button
                onClick={confirmDelete}
                className='w-full bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700 text-white font-medium py-3 rounded-lg transition-colors duration-150'
              >
                네
              </button>
              <button
                onClick={closeDeleteModal}
                className='w-full text-amber-700 hover:bg-amber-50 font-medium py-3 rounded-lg transition-colors duration-150 border border-amber-200'
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ChatHistorySidebar
